let csrfToken = "";
let csrfRefresh = null;

export class ApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

export function setCsrf(value) {
  csrfToken = value || "";
}

export async function api(path, options = {}, retryCSRF = true) {
  const headers = new Headers(options.headers || {});
  const method = (options.method || "GET").toUpperCase();
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const response = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: "same-origin"
  });
  let payload = null;
  if (response.status !== 204) {
    const type = response.headers.get("content-type") || "";
    payload = type.includes("application/json") ? await response.json() : await response.text();
  }
  if (!response.ok) {
    if (retryCSRF && !["GET", "HEAD", "OPTIONS"].includes(method) &&
        response.status === 403 && payload?.error?.code === "csrf_invalid") {
      csrfRefresh ||= fetch("/api/v1/session", { credentials: "same-origin", cache: "no-store" })
        .then(async (refreshResponse) => {
          if (!refreshResponse.ok) throw new ApiError("Die Sitzung ist abgelaufen.", "authentication_required", refreshResponse.status);
          const envelope = await refreshResponse.json();
          setCsrf(envelope.csrf_token);
        })
        .finally(() => { csrfRefresh = null; });
      await csrfRefresh;
      return api(path, options, false);
    }
    throw new ApiError(
      payload?.error?.message || "Die Verbindung zur Wissensbande ist fehlgeschlagen.",
      payload?.error?.code || "request_failed",
      response.status
    );
  }
  return payload;
}

export async function downloadProfileExport(profile) {
  const response = await fetch(
    "/api/v1/parent/profiles/" + encodeURIComponent(profile.id) + "/export",
    { credentials: "same-origin" }
  );
  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // Preserve the same safe fallback used by api().
    }
    throw new ApiError(
      payload?.error?.message || "Der Profil-Export ist fehlgeschlagen.",
      payload?.error?.code || "request_failed",
      response.status
    );
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${profile.profile_key}-wissensbande.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export function installLearningBridge(getSession, closeLearning, receiveGameEvent = (_event) => {}) {
  const listeners = new Map();
  const tabId = crypto.randomUUID();
  const leaseLifetimeMs = 12_000;
  let leaseTimer = 0;
  let epoch = 0;
  let active = null;
  let pendingClose = null;
  const motionQuery = matchMedia("(prefers-reduced-motion: reduce)");

  const leaseKey = (context) => `wissensbande.active.v1:${context.householdId}:${context.profile.id}`;
  const readLease = (context) => {
    try { return JSON.parse(localStorage.getItem(leaseKey(context)) || "null"); } catch { return null; }
  };
  const ownsLease = (context = active) => {
    const lease = context ? readLease(context) : null;
    return Boolean(context && lease?.tab_id === tabId && lease?.activation_id === context.activationId);
  };
  const writeLease = (context) => {
    if (!context) return false;
    const previous = readLease(context);
    if (previous && previous.tab_id !== tabId && Number(previous.expires_at) > Date.now()) return false;
    try {
      localStorage.setItem(leaseKey(context), JSON.stringify({ tab_id: tabId, activation_id: context.activationId, expires_at: Date.now() + leaseLifetimeMs }));
      return ownsLease(context);
    } catch {
      return false;
    }
  };
  const releaseLease = (context = active) => {
    if (context && ownsLease(context)) {
      try { localStorage.removeItem(leaseKey(context)); } catch { /* no durable browser storage */ }
    }
    context?.releaseLock?.();
  };
  const acquireLease = async (context) => {
    /** @type {(() => void) | null} */
    let releaseLock = null;
    if (navigator.locks?.request) {
      const name = leaseKey(context);
      const acquired = await new Promise((resolve) => {
        navigator.locks.request(name, { mode: "exclusive", ifAvailable: true }, (lock) => {
          if (!lock) { resolve(false); return; }
          resolve(true);
          return new Promise((release) => { releaseLock = () => release(true); });
        }).catch(() => resolve(false));
      });
      if (!acquired) return null;
    }
    if (writeLease(context)) return releaseLock || (() => {});
    if (typeof releaseLock === "function") releaseLock();
    return null;
  };
  const current = (requestedEpoch) => {
    if (!active) throw new ApiError("Die Lernmission wurde geschlossen.", "bridge_inactive", 410);
    if (Number(requestedEpoch) !== active.epoch) throw new ApiError("Die Lernmission wurde ersetzt.", "bridge_epoch_invalid", 410);
    if (!ownsLease(active)) throw new ApiError("Diese Mission ist bereits in einem anderen Tab geöffnet.", "tab_conflict", 409);
    return active;
  };
  const callbackError = (callback, error) => callback(JSON.stringify({
    ok: false,
    status: Number(error?.status || 0),
    error: String(error?.code || "request_failed")
  }));
  const publish = (event) => {
    const listener = listeners.get(Number(event.epoch));
    if (!listener) return;
    try { listener(JSON.stringify(event)); } catch { listeners.delete(Number(event.epoch)); }
  };

  /** @type {any} */ (window).WissensbandeHost = Object.freeze({
    version: "wissensbande.host.v2",
    contextJson(requestedEpoch) {
      try {
        const context = current(requestedEpoch);
        return JSON.stringify({
          schema_version: "wissensbande.web-context.v1",
          epoch: context.epoch,
          household_id: context.householdId,
          profile_id: context.profile.id,
          profile_key: context.profile.profile_key,
          profile: {
            id: context.profile.id,
            profile_key: context.profile.profile_key,
            display_name: context.profile.display_name,
            grade: context.profile.grade,
            avatar_key: context.profile.avatar_key,
            revision: context.profile.revision
          },
          commerce: context.commerce || null,
          online: navigator.onLine,
          reduced_motion: motionQuery.matches,
          text_scale: Number(localStorage.getItem("wissensbande.text-scale") || "1")
        });
      } catch (error) {
        return JSON.stringify({ ok: false, error: String(error?.code || "bridge_inactive") });
      }
    },
    requestSnapshot(requestedEpoch, callback) {
      let context;
      try { context = current(requestedEpoch); } catch (error) { callbackError(callback, error); return; }
      api("/api/v1/profiles/" + encodeURIComponent(context.profile.id) + "/snapshot", { signal: context.abort.signal })
        .then((body) => {
          if (active?.epoch !== context.epoch) throw new ApiError("Die Lernmission wurde geschlossen.", "bridge_inactive", 410);
          callback(JSON.stringify({ ok: true, status: 200, body }));
        })
        .catch((error) => callbackError(callback, error));
    },
    requestSync(requestedEpoch, requestJson, callback) {
      let context;
      try { context = current(requestedEpoch); } catch (error) { callbackError(callback, error); return; }
      if (typeof requestJson !== "string" || requestJson.length > 1024 * 1024) {
        callback(JSON.stringify({ ok: false, status: 0, error: "invalid_bridge_request" }));
        return;
      }
      let request;
      try {
        request = JSON.parse(requestJson);
      } catch {
        callback(JSON.stringify({ ok: false, status: 0, error: "invalid_bridge_request" }));
        return;
      }
      if (request?.schema_version !== "wissensbande.sync.v1") {
        callback(JSON.stringify({ ok: false, status: 0, error: "invalid_bridge_request" }));
        return;
      }
      api("/api/v1/profiles/" + encodeURIComponent(context.profile.id) + "/sync", {
        method: "POST",
        body: JSON.stringify(request),
        signal: context.abort.signal
      })
        .then((body) => {
          if (active?.epoch !== context.epoch) throw new ApiError("Die Lernmission wurde geschlossen.", "bridge_inactive", 410);
          callback(JSON.stringify({ ok: true, status: 200, body }));
        })
        .catch((error) => callbackError(callback, error));
    },
    notify(requestedEpoch, eventJson) {
      const context = current(requestedEpoch);
      if (typeof eventJson !== "string" || eventJson.length > 4096) return false;
      let event;
      try { event = JSON.parse(eventJson); } catch { return false; }
      const allowed = new Set(["runtime-ready", "progress-local", "progress-synced", "sync-settled", "connection", "safe-error"]);
      if (event?.schema_version !== "wissensbande.host-event.v1" || !allowed.has(event.type)) return false;
      receiveGameEvent({ ...event, epoch: context.epoch });
      return true;
    },
    acknowledgeClose(closingEpoch, saved) {
      if (pendingClose && Number(closingEpoch) === pendingClose.epoch) {
        pendingClose.resolve(Boolean(saved));
        pendingClose = null;
      }
    },
    close(requestedEpoch) {
      current(requestedEpoch);
      closeLearning();
    },
    subscribe(requestedEpoch, listener) {
      current(requestedEpoch);
      if (typeof listener !== "function") return false;
      listeners.set(Number(requestedEpoch), listener);
      return true;
    },
    unsubscribe(requestedEpoch) {
      return listeners.delete(Number(requestedEpoch));
    }
  });

  const deactivate = () => {
    const closing = active;
    const closingEpoch = closing?.epoch;
    closing?.abort.abort();
    releaseLease(closing);
    window.clearInterval(leaseTimer);
    leaseTimer = 0;
    if (closingEpoch !== undefined) publish({ type: "profile-invalidated", epoch: closingEpoch });
    listeners.delete(Number(closingEpoch));
    active = null;
    epoch += 1;
  };
  const handleOnline = () => active && publish({ type: "connection", epoch: active.epoch, online: true });
  const handleOffline = () => active && publish({ type: "connection", epoch: active.epoch, online: false });
  const handleStorage = (event) => {
    if (active && event.key === "wissensbande.text-scale") {
      publish({ type: "preferences", epoch: active.epoch, reduced_motion: motionQuery.matches, text_scale: Number(localStorage.getItem("wissensbande.text-scale") || "1") });
    }
    if (active && event.key === leaseKey(active) && !ownsLease(active)) {
      publish({ type: "tab-conflict", epoch: active.epoch });
    }
  };
  const handleMotion = () => active && publish({ type: "preferences", epoch: active.epoch, reduced_motion: motionQuery.matches, text_scale: Number(localStorage.getItem("wissensbande.text-scale") || "1") });
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  window.addEventListener("storage", handleStorage);
  window.addEventListener("pagehide", releaseLease);
  motionQuery.addEventListener?.("change", handleMotion);

  return Object.freeze({
    async activate(profile) {
      deactivate();
      const session = getSession();
      const householdId = String(session?.household?.id || "");
      if (!profile?.id || !profile?.profile_key || !householdId) {
        throw new Error("Authenticated household and profile are required for the learning bridge.");
      }
      const candidate = { profile: structuredClone(profile), commerce: structuredClone(session?.commerce || null), householdId, epoch, activationId: crypto.randomUUID(), abort: new AbortController(), releaseLock: null };
      const releaseLock = await acquireLease(candidate);
      if (!releaseLock) {
        throw new ApiError("Diese Mission ist bereits in einem anderen Tab geöffnet.", "tab_conflict", 409);
      }
      candidate.releaseLock = releaseLock;
      if (candidate.epoch !== epoch || active) {
        releaseLease(candidate);
        throw new ApiError("Die Lernmission wurde ersetzt.", "bridge_epoch_invalid", 410);
      }
      active = candidate;
      leaseTimer = window.setInterval(() => {
        if (!writeLease(candidate)) publish({ type: "tab-conflict", epoch: candidate.epoch });
      }, leaseLifetimeMs / 3);
      publish({ type: "profile-activated", epoch: candidate.epoch, online: navigator.onLine });
      return candidate.epoch;
    },
    prepareClose(timeoutMs = 1500) {
      if (!active) return Promise.resolve(true);
      const closingEpoch = active.epoch;
      return new Promise((resolve) => {
        const timeout = window.setTimeout(() => {
          if (pendingClose?.epoch === closingEpoch) pendingClose = null;
          resolve(false);
        }, timeoutMs);
        pendingClose = {
          epoch: closingEpoch,
          resolve(saved) { window.clearTimeout(timeout); resolve(saved); }
        };
        publish({ type: "close-request", epoch: closingEpoch });
      });
    },
    deactivate,
    publish,
    destroy() {
      deactivate();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("pagehide", releaseLease);
      motionQuery.removeEventListener?.("change", handleMotion);
      delete /** @type {any} */ (window).WissensbandeHost;
    }
  });
}
